import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ShippingInfoCard, EMPTY_SHIPPING, cleanShipping } from '../components/ShippingInfoCard';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EditDeliveryOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [documentNumber, setDocumentNumber] = useState('');
  const [shipping, setShipping] = useState(EMPTY_SHIPPING);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    po_number: '',
    delivery_address: '',
    pickup_address: '',
    pickup_contact: '',
    site_contact: '',
    site_phone: '',
    delivery_date: '',
    delivery_time: '',
    line_items: [],
    notes: ''
  });

  useEffect(() => {
    fetchDeliveryOrder();
  }, [id]);

  const fetchDeliveryOrder = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/delivery-orders/${id}`);
      const deliveryOrder = response.data;
      setDocumentNumber(deliveryOrder.document_number);
      setFormData({
        customer_name: deliveryOrder.customer_name || '',
        customer_email: deliveryOrder.customer_email || '',
        customer_phone: deliveryOrder.customer_phone || '',
        po_number: deliveryOrder.po_number || '',
        delivery_address: deliveryOrder.delivery_address || '',
        pickup_address: deliveryOrder.pickup_address || '',
        pickup_contact: deliveryOrder.pickup_contact || '',
        site_contact: deliveryOrder.site_contact || deliveryOrder.site_contact_name || '',
        site_phone: deliveryOrder.site_phone || deliveryOrder.site_contact_phone || '',
        delivery_date: deliveryOrder.delivery_date || '',
        delivery_time: deliveryOrder.delivery_time || '',
        line_items: deliveryOrder.line_items || [],
        notes: deliveryOrder.notes || ''
      });
      setShipping({ ...EMPTY_SHIPPING, ...(deliveryOrder.shipping_info || {}) });
    } catch (error) {
      toast.error('Failed to load delivery order');
      navigate('/admin/billing');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        { description: '', quantity: 1, hours: 0, unit_price: 0, amount: 0 }
      ]
    });
  };

  const removeLineItem = (index) => {
    const newItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'unit_price' || field === 'hours') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const hours = parseFloat(newItems[index].hours) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].amount = hours > 0 ? qty * hours * price : qty * price;
    }
    
    setFormData({ ...formData, line_items: newItems });
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const gst = 0; // GST disabled
    const total = subtotal + gst;
    return { subtotal, gst, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const recalcItems = formData.line_items.map(item => {
        const qty = parseFloat(item.quantity) || 0;
        const hours = parseFloat(item.hours) || 0;
        const price = parseFloat(item.unit_price) || 0;
        return {
          description: item.description,
          lorry_no: item.lorry_no || '',
          quantity: qty,
          hours,
          unit_price: price,
          amount: hours > 0 ? qty * hours * price : qty * price
        };
      });
      const subtotal = recalcItems.reduce((sum, i) => sum + i.amount, 0);

      const updateData = {
        ...formData,
        shipping_info: cleanShipping(shipping),
        document_number: documentNumber,
        line_items: recalcItems,
        subtotal,
        gst_amount: 0,
        total_amount: subtotal
      };

      const response = await axios.put(`${BACKEND_URL}/api/delivery-orders/${id}`, updateData);
      
      if (response.data.success) {
        toast.success('Delivery order updated successfully');
        navigate(`/admin/billing/delivery-order/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery order');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, total } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/billing/delivery-order/${id}`)}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Edit Delivery Order</h1>
          </div>
          <span className="text-lg font-semibold text-orange-600">{documentNumber}</span>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Document Number</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-w-xs space-y-2">
                <Label htmlFor="document_number">DO Number</Label>
                <Input
                  id="document_number"
                  data-testid="edit-do-number-input"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                />
                <p className="text-xs text-gray-500">Change carefully — duplicates are rejected automatically.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Customer Name</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_phone">Phone</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="customer_email">Email</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="po_number">Customer P/O Number</Label>
                  <Input
                    id="po_number"
                    value={formData.po_number}
                    onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                    placeholder="Purchase Order number (optional)"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Delivery Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pickup_address">Pickup Address</Label>
                  <Textarea
                    id="pickup_address"
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_address">Delivery Address</Label>
                  <Textarea
                    id="delivery_address"
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_date">Delivery Date</Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_time">Delivery Time</Label>
                  <Input
                    id="delivery_time"
                    type="time"
                    value={formData.delivery_time}
                    onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pickup_contact">Pickup Contact</Label>
                  <Input
                    id="pickup_contact"
                    value={formData.pickup_contact}
                    onChange={(e) => setFormData({ ...formData, pickup_contact: e.target.value })}
                    placeholder="e.g., Mr Lim 9123 4567"
                    data-testid="edit-pickup-contact"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site_contact">Site Contact Name</Label>
                  <Input
                    id="site_contact"
                    value={formData.site_contact}
                    onChange={(e) => setFormData({ ...formData, site_contact: e.target.value })}
                    placeholder="e.g., Mr Tan"
                    data-testid="edit-site-contact"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site_phone">Site Contact Phone</Label>
                  <Input
                    id="site_phone"
                    value={formData.site_phone}
                    onChange={(e) => setFormData({ ...formData, site_phone: e.target.value })}
                    placeholder="e.g., 9123 4567"
                    data-testid="edit-site-phone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Line Items</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus size={16} className="mr-2" />
                Add Item
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.line_items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Supply of 25-ton lorry crane / trailer / tailgate / manpower..."
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Lorry No.</Label>
                      <Input
                        value={item.lorry_no || ''}
                        onChange={(e) => updateLineItem(index, 'lorry_no', e.target.value)}
                        placeholder="XD1234A"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        min="0"
                        step="1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Hours</Label>
                      <Input
                        type="number"
                        value={item.hours || ''}
                        onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                        min="0"
                        step="0.5"
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}

                {formData.line_items.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No line items. Click "Add Item" to add services.</p>
                )}

              </div>
            </CardContent>
          </Card>

          <ShippingInfoCard value={shipping} onChange={setShipping} />

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/admin/billing/delivery-order/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
